import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Alert,
  TextField,
  Typography,
} from '@mui/material';

import { SectionCard, SectionHeader } from '@/components/common';
import {
  useDelegateAppraisal,
  useRevokeAppraisalDelegate,
  useEligibleProxies,
} from '@/queries/useAppraisalDetail';
import type { AppraisalDetailResponse, EligibleProxy } from '@/api/types/appraisal.types';
import type { RecordAccess } from '@/auth/permissions.types';

interface AppraisalDetailsSectionProps {
  appraisalDetail: AppraisalDetailResponse;
  permissions: RecordAccess | undefined;
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start || !end) return '-';
  const format = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  };
  return `${format(start)} - ${format(end)}`;
}

export function AppraisalDetailsSection({ appraisalDetail, permissions }: AppraisalDetailsSectionProps) {
  const [selectedProxy, setSelectedProxy] = useState<EligibleProxy | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const { mutate: delegateAppraisal, isPending: isDelegating } = useDelegateAppraisal();
  const { mutate: revokeDelegate, isPending: isRevoking } = useRevokeAppraisalDelegate();
  const { data: eligibleProxies = [] } = useEligibleProxies(
    String(appraisalDetail.id),
    inputValue
  );

  const canDelegate = permissions?.actions?.canDelegate ?? false;
  const hasDelegate = !!appraisalDetail.delegateName;

  // Button enabled only when a different proxy is selected
  const isNewSelection = selectedProxy != null
    && selectedProxy.staffId !== appraisalDetail.delegateStaffId;

  const handleAddProxyClick = () => {
    if (!isNewSelection) return;
    setDialogOpen(true);
  };

  const handleConfirmDelegation = () => {
    if (!selectedProxy) return;

    const proxyName = `${selectedProxy.firstName} ${selectedProxy.lastName}`;
    setDialogOpen(false);

    delegateAppraisal(
      { appraisalId: appraisalDetail.id, delegateStaffId: selectedProxy.staffId, rowVersion: appraisalDetail.rowVersion },
      {
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: `${proxyName} has been added as a Proxy to this appraisal.`,
          });
          setSelectedProxy(null);
          setInputValue('');
        },
        onError: () => {
          setSnackbar({
            open: true,
            message: 'Failed to add proxy.',
          });
        },
      }
    );
  };

  const handleRemoveProxy = () => {
    const proxyName = appraisalDetail.delegateName;
    revokeDelegate({ appraisalId: appraisalDetail.id, rowVersion: appraisalDetail.rowVersion }, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: `${proxyName} has been removed as a Proxy and no longer has edit access to this appraisal.`,
        });
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: 'Failed to remove proxy.',
        });
      },
    });
  };

  const showWeeksStaffed = appraisalDetail.staffingWeeks > 0;

  return (
    <>
      <Box data-testid="appraisal-details-sticky" sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
        <SectionHeader>APPRAISAL DETAILS</SectionHeader>
        <SectionCard sx={canDelegate ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : undefined}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: { xs: 2, md: 3 },
            }}
          >
            <DetailItem label="Appraisee" value={`${appraisalDetail.appraiseeFirstName} ${appraisalDetail.appraiseeLastName}`} />
            <DetailItem label="Office" value={appraisalDetail.office || '-'} />
            <DetailItem label="Tenure in Grade" value={appraisalDetail.tenureInGrade || '-'} />
            <DetailItem label="Collator" value={appraisalDetail.collatorName || '-'} />
            <DetailItem label="Mentor" value={appraisalDetail.mentorName || '-'} />
            <DetailItem
              label="Appraisal Period"
              value={formatPeriod(appraisalDetail.appraisalPeriodStart, appraisalDetail.appraisalPeriodEnd)}
            />
            <DetailItem label="Weeks Staffed" value={showWeeksStaffed ? String(appraisalDetail.staffingWeeks) : '-'} />
            <DetailItem label="Last appraisal rating" value={appraisalDetail.lastRating || '-'} />
          </Box>
        </SectionCard>
      </Box>

      {canDelegate && (
        /* Outside the sticky box so it scrolls away under it; the negative margin
           cancels the column gap of AppLayout's <main> so the two still read as one panel. */
        <Paper
          variant="outlined"
          data-testid="appraisal-proxy-panel"
          sx={{
            mt: { xs: -2, md: -3 },
            p: 2,
            borderTop: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
            <Box sx={{ minWidth: 220 }}>
              {hasDelegate && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Assigned Proxy: {appraisalDetail.delegateName}
                </Typography>
              )}
              <Typography variant="body1" color="text.secondary" display="block">
                {hasDelegate ? 'Change Proxy' : 'Add Proxy'}
              </Typography>
              <Autocomplete
                size="small"
                sx={{ minWidth: 220 }}
                options={eligibleProxies}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                isOptionEqualToValue={(option, value) => option.staffId === value.staffId}
                value={selectedProxy}
                inputValue={inputValue}
                onChange={(_, value) => {
                  setSelectedProxy(value);
                  setInputValue(value ? `${value.firstName} ${value.lastName}` : '');
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setInputValue(value);
                    if (reason === 'clear') setSelectedProxy(null);
                  }
                }}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={hasDelegate ? appraisalDetail.delegateName ?? 'Select person' : 'Select person'}
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              disabled={!isNewSelection || isDelegating}
              onClick={handleAddProxyClick}
              sx={{ whiteSpace: 'nowrap', height: 40 }}
            >
              {hasDelegate ? 'CHANGE PROXY' : 'ADD PROXY'}
            </Button>
            {hasDelegate && (
              <Button
                variant="outlined"
                color="error"
                disabled={isRevoking}
                onClick={handleRemoveProxy}
                sx={{ whiteSpace: 'nowrap', height: 40 }}
              >
                REMOVE PROXY
              </Button>
            )}
          </Box>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Add Proxy</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to give a Proxy edit access to this appraisal form. This will allow them to update the form on your behalf.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelegation} disabled={isDelegating}>
            Add Proxy
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, message: '' })}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: '' })}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="body1" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
}

export default AppraisalDetailsSection;
